import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ChevronRight, CheckCircle2 } from "lucide-react";
import { DEVIS_LEAD, DevisRecap } from "@/components/ApplyDevisButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OptionPills = ({ options, value, onChange, name }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {options.map((o) => (
      <button
        key={o.v}
        type="button"
        data-testid={`lead-${name}-${o.v}`}
        onClick={() => onChange(o.v)}
        className={`text-left rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
          value === o.v
            ? "border-[#9E7B41] bg-[#faf7f1] text-[#2b2b2b]"
            : "border-border bg-white text-muted-foreground hover:border-[#c9b184]"
        }`}
      >
        {o.t}
      </button>
    ))}
  </div>
);

export default function LeadForm({ simulationResume, answers, onSubmitted }) {
  const [f, setF] = useState({
    projet: "", projet_achat: "", van_reprendre: "", nb_chevaux: "", permis: "",
    nom: "", prenom: "", tel: "", email: "", adresse: "", code_postal: "", ville: "", pays: "France",
    consent_newsletter: false, consent_sms: false, website: "", societe: "", montant_reprise: null,
    ...DEVIS_LEAD,
    ...(answers?.code_postal && !DEVIS_LEAD.code_postal ? { code_postal: answers.code_postal } : {}),
  });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  const longTerme = f.projet_achat === "plus_1_an";
  const askChevaux = f.projet !== "fourgon";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.projet || !f.projet_achat) { toast.error("Merci de compléter les premières questions."); return; }
    if (!longTerme && (!f.van_reprendre || (askChevaux && !f.nb_chevaux))) {
      toast.error("Merci de répondre à toutes les questions de qualification."); return;
    }
    for (const [k, lbl] of [["nom","Nom"],["prenom","Prénom"],["tel","Téléphone"],["email","Email"],["adresse","Adresse"],["code_postal","Code postal"],["ville","Ville"],["pays","Pays"]]) {
      if (!String(f[k]).trim()) { toast.error(`Champ requis : ${lbl}.`); return; }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) {
      toast.error("Merci de saisir une adresse e-mail valide (ex. nom@domaine.fr)."); return;
    }
    setBusy(true);
    try {
      const payload = {
        projet: f.projet,
        projet_achat: f.projet_achat,
        van_reprendre: longTerme ? null : f.van_reprendre,
        nb_chevaux: longTerme || !askChevaux ? null : parseFloat(f.nb_chevaux),
        societe: f.societe.trim() || null,
        montant_reprise: !longTerme && f.van_reprendre === "oui" ? f.montant_reprise : null,
        permis: longTerme || !f.permis ? null : f.permis,
        nom: f.nom.trim(), prenom: f.prenom.trim(), tel: f.tel.trim(), email: f.email.trim(),
        adresse: f.adresse.trim(), code_postal: f.code_postal.trim(), ville: f.ville.trim(), pays: f.pays.trim(),
        consent_newsletter: f.consent_newsletter, consent_sms: f.consent_sms,
        simulation: { resume: simulationResume || "", answers: answers || null },
        website: f.website,
      };
      const { data } = await axios.post(`${API}/leads`, payload);
      toast.success("Merci ! Votre demande a été transmise à votre vendeur.");
      onSubmitted(data.vendeur);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      data-testid="lead-form"
      noValidate
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="lead-website">Site web</label>
        <input id="lead-website" name="website" type="text" tabIndex={-1} autoComplete="off"
          value={f.website} onChange={(e) => set({ website: e.target.value })} data-testid="lead-honeypot-input" />
      </div>
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#9E7B41]/15 text-[#9E7B41] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
          <Lock className="h-3.5 w-3.5" /> Dernière étape
        </span>
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#2b2b2b] mt-4">
          Plus qu'une étape avant d'accéder à la simulation…
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          Laissez vos coordonnées : votre simulation s'affiche immédiatement et un conseiller de votre secteur vous recontacte.
        </p>
      </div>

      {answers?.depuis_devis && <DevisRecap answers={answers} projet={f.projet} />}
      <div className="space-y-6 bg-white rounded-2xl border border-border p-5 sm:p-7">
        <div>
          <Label className="text-sm font-semibold">Mon projet concerne</Label>
          <div className="mt-2">
            <OptionPills name="projet" value={f.projet} onChange={(v) => set({ projet: v })}
              options={[{ v: "van", t: "Un van tracté pour chevaux" }, { v: "fourgon", t: "Une remorque fourgon Pro&Sport" }]} />
          </div>
        </div>

        <div>
          <Label className="text-sm font-semibold">J'ai un projet d'achat</Label>
          <div className="mt-2">
            <OptionPills name="achat" value={f.projet_achat} onChange={(v) => set({ projet_achat: v })}
              options={[
                { v: "immediat", t: "Oui, immédiat" },
                { v: "moins_3_mois", t: "Oui, dans moins de 3 mois" },
                { v: "moins_1_an", t: "Oui, dans moins d'un an" },
                { v: "plus_1_an", t: "Dans plus d'un an" },
              ]} />
          </div>
        </div>

        {!longTerme && f.projet_achat && (
          <>
            <div>
              <Label className="text-sm font-semibold">Je suis propriétaire d'un véhicule à reprendre</Label>
              <div className="mt-2">
                <OptionPills name="reprise" value={f.van_reprendre} onChange={(v) => set({ van_reprendre: v })}
                  options={[{ v: "non", t: "Non" }, { v: "oui", t: "Oui" }]} />
              </div>
            </div>
            {askChevaux && <div>
              <Label className="text-sm font-semibold">Combien je souhaite en transporter</Label>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
                {["1", "1.5", "2", "3", "4", "5"].map((n) => (
                  <button key={n} type="button" data-testid={`lead-chevaux-${n.replace(".", "-")}`}
                    onClick={() => set({ nb_chevaux: n })}
                    className={`rounded-xl border-2 py-3 text-sm font-semibold transition-colors ${
                      f.nb_chevaux === n ? "border-[#9E7B41] bg-[#faf7f1] text-[#2b2b2b]" : "border-border bg-white text-muted-foreground"
                    }`}>
                    {n.replace(".", ",")} {n === "1" ? "cheval" : "chevaux"}
                  </button>
                ))}
              </div>
            </div>}
            <div>
              <Label className="text-sm font-semibold">J'ai le permis (ou le projet de le passer) <span className="font-normal text-muted-foreground">(facultatif)</span></Label>
              <div className="mt-2">
                <OptionPills name="permis" value={f.permis} onChange={(v) => set({ permis: v })}
                  options={[{ v: "b", t: "Permis B uniquement" }, { v: "b96", t: "Formation B96" }, { v: "be", t: "Permis BE" }]} />
              </div>
            </div>
          </>
        )}

        <div className="h-px bg-border" />
        <p className="text-xs font-semibold uppercase tracking-wider text-[#9E7B41]">Mes coordonnées</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Prénom" k="prenom" f={f} set={set} />
          <Field label="Nom" k="nom" f={f} set={set} />
          <div className="sm:col-span-2"><Field label="Société (facultatif)" k="societe" f={f} set={set} /></div>
          <Field label="Téléphone" k="tel" f={f} set={set} type="tel" />
          <Field label="Email" k="email" f={f} set={set} type="email" />
          <div className="sm:col-span-2"><Field label="Adresse" k="adresse" f={f} set={set} /></div>
          <Field label="Code postal" k="code_postal" f={f} set={set} />
          <Field label="Ville" k="ville" f={f} set={set} />
          <Field label="Pays" k="pays" f={f} set={set} />
        </div>

        <div className="space-y-3 pt-1">
          <Consent checked={f.consent_newsletter} onChange={(v) => set({ consent_newsletter: v })} testId="lead-consent-newsletter">
            J'accepte de recevoir la newsletter de Fautras et des emails ciblés (enquêtes, nouveautés produits…). Désinscription possible à tout moment via les liens présents dans chaque envoi.
          </Consent>
          <Consent checked={f.consent_sms} onChange={(v) => set({ consent_sms: v })} testId="lead-consent-sms">
            Je souhaite recevoir les infos sur les opérations spéciales (promotions, réductions, jeux concours…) par SMS. Désinscription possible via une réponse SMS.
          </Consent>
        </div>

        <Button type="submit" disabled={busy} data-testid="lead-submit"
          className="w-full h-12 bg-[#9E7B41] hover:bg-[#876634] rounded-xl font-semibold text-base">
          {busy ? "Envoi…" : <>Accéder à ma simulation <ChevronRight className="h-4 w-4 ml-1" /></>}
        </Button>
        <p className="text-[11px] text-center text-muted-foreground">
          Vos données sont transmises au vendeur Fautras de votre secteur. Voir notre{" "}
          <a href="https://fautras.com" target="_blank" rel="noopener noreferrer" className="text-[#9E7B41] hover:underline">politique de confidentialité</a>.
        </p>
      </div>
    </motion.form>
  );
}

const Field = ({ label, k, f, set, type = "text" }) => (
  <div>
    <Label htmlFor={`lead-${k}`} className="text-sm font-semibold">{label}</Label>
    <Input id={`lead-${k}`} type={type} value={f[k]} data-testid={`lead-${k}`}
      onChange={(e) => set({ [k]: e.target.value })} className="mt-2 h-11" />
  </div>
);

const Consent = ({ checked, onChange, children, testId }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input type="checkbox" checked={checked} data-testid={testId}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 accent-[#9E7B41] shrink-0" />
    <span className="text-xs text-muted-foreground leading-relaxed">{children}</span>
  </label>
);
